/**
 * "Tests using this Set" panel — the reverse view that lists
 * every test which reads from the current TestDataSet.
 *
 * Renders inside the TestDataSetEditor as the "Tests using this
 * Set" tab. Each row shows the test name, iteration shape
 * ("N = 1" / "N = 5" / "All rows (247)"), consumption flag, and
 * a click-through to the per-binding detail page.
 *
 * Vocabulary lock (post-merge corrections): "Used by" / "Tests
 * using this Set" everywhere in operator-visible strings. Code
 * identifiers stay (`DataBinding`, `bindingId`).
 *
 * Post-merge corrections add a "+ Add a test" button so an
 * operator can manually attach a script even when the auto-create
 * hook didn't fire (legacy tests scripted before #1262 landed).
 * The picker dialog uses the `/data-bindings/unbound-scripts`
 * endpoint to list eligible scripts; selecting one creates the
 * DataBinding and routes to the binding-detail editor.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ExternalLink,
  AlertTriangle,
  Archive,
  Plus,
  Search,
  Link2,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui'
import {
  dataBindingsAPI,
  type DataBindingForSet,
  type UnboundScriptSummary,
} from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'

export function TestDataBoundTestsPanel({
  setId,
}: {
  setId: string
}) {
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const toast = useToast()

  const [bindings, setBindings] = useState<DataBindingForSet[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [bindOpen, setBindOpen] = useState(false)

  async function refresh() {
    try {
      const data = await dataBindingsAPI.listForSet(setId)
      setBindings(data.bindings)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      toast({
        title: 'Failed to load tests using this Set',
        description: err instanceof Error ? err.message : String(err),
        variant: 'error',
      })
    }
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setId])

  function handleBindCreated(scriptId: string) {
    setBindOpen(false)
    // Land on the binding editor with this Set + the freshly
    // picked script so the operator can confirm the mapper.
    navigate(wp(`/test-data/${setId}/bindings/${scriptId}`))
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="p-4 border-destructive bg-destructive/10 text-destructive text-sm">
          {error}
        </Card>
      </div>
    )
  }

  if (bindings === null) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {bindings.length === 0
            ? 'No tests are using this Set yet.'
            : `Used by ${bindings.length} test${bindings.length === 1 ? '' : 's'}.`}
        </p>
        <Button size="sm" onClick={() => setBindOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add a test
        </Button>
      </div>

      {bindings.length === 0 ? (
        <Card className="p-8 text-center">
          <Link2 className="h-10 w-10 text-muted-foreground/60 mx-auto mb-3" />
          <h3 className="text-sm font-medium mb-1">No tests are using this Set yet</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto mb-4">
            Tests are added here automatically when OttoTester generates a script that
            reads from these columns. You can also add one manually below.
          </p>
          <Button size="sm" onClick={() => setBindOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add a test
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Test</th>
                <th className="text-left px-3 py-2 font-medium">Consumption</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => (
                <BoundTestRow
                  key={b._id}
                  binding={b}
                  onOpen={() => navigate(wp(`/test-data/${setId}/bindings/${b.scriptId}`))}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <BindTestDialog
        open={bindOpen}
        setId={setId}
        onClose={() => setBindOpen(false)}
        onBindCreated={handleBindCreated}
      />
    </div>
  )
}

function BoundTestRow({
  binding,
  onOpen,
}: {
  binding: DataBindingForSet
  onOpen: () => void
}) {
  const consLabel = binding.consumption === 'single-use' ? 'Single-use' : 'Reusable'

  return (
    <tr
      className="border-t hover:bg-muted/30 cursor-pointer focus:outline-none focus:bg-muted/40 transition-colors"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open ${binding.scriptName}`}
    >
      <td className="px-3 py-3">
        <div className="font-medium">{binding.scriptName}</div>
        <div className="text-xs text-muted-foreground mt-0.5 font-mono">
          {Object.keys(binding.fieldMappings).length} field mapping
          {Object.keys(binding.fieldMappings).length === 1 ? '' : 's'}
        </div>
      </td>
      <td className="px-3 py-3">
        {consLabel}
        {binding.consumption === 'single-use' && (
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Shared across tests using this Set
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        {binding.wasOrphaned && (
          <Badge variant="outline" className="text-amber-700 border-amber-400 text-[10px]">
            <AlertTriangle className="h-3 w-3 mr-1" /> orphaned
          </Badge>
        )}
        {binding.archived && (
          <Badge variant="outline" className="text-muted-foreground text-[10px]">
            <Archive className="h-3 w-3 mr-1" /> archived
          </Badge>
        )}
        {!binding.wasOrphaned && !binding.archived && (
          <Badge variant="outline" className="text-emerald-700 border-emerald-400 text-[10px]">
            active
          </Badge>
        )}
      </td>
      <td className="px-3 py-3 text-right text-muted-foreground text-xs">
        {new Date(binding.updatedAt).toLocaleString()}
      </td>
      <td className="px-3 py-3 w-12">
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
          aria-label="Open this test"
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

// ── Add-a-test dialog ────────────────────────────────────────

/** Picker dialog — searchable list of unbound scripts in the
 * workspace. Selecting one creates the binding (with auto-mapper
 * pre-populated by the binding-detail page) and routes to the
 * binding editor.
 *
 * The dialog doesn't create the binding itself — it routes to
 * the binding-detail page which already knows how to create a
 * fresh binding on save. This keeps the binding-creation logic
 * in one place (the detail page handles the optimistic-mapper
 * pre-population, the user's confirm-on-save discipline, and
 * the audit row). The dialog's job is just to pick the script. */
// The dialog doesn't need `setId` itself — the parent uses it when
// constructing the navigation target after a script is picked. We
// keep `setId` on the prop type for documentation but don't
// destructure it; that drops the lint warning cleanly without a
// no-op expression. Copilot review C4 on PR #1274.
function BindTestDialog({
  open,
  onClose,
  onBindCreated,
}: {
  open: boolean
  setId: string
  onClose: () => void
  onBindCreated: (scriptId: string) => void
}) {
  const [scripts, setScripts] = useState<UnboundScriptSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    setScripts(null)
    setError(null)
    setQuery('')
    let cancelled = false
    dataBindingsAPI
      .listUnboundScripts()
      .then((data) => {
        if (cancelled) return
        setScripts(data.scripts)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const filtered = useMemo(() => {
    if (!scripts) return []
    const q = query.trim().toLowerCase()
    if (!q) return scripts
    return scripts.filter((s) => s.name.toLowerCase().includes(q))
  }, [scripts, query])

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a test that uses this Set</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-xs text-muted-foreground mb-3">
            Pick a test that doesn't yet read from any Test Data Set.
            You'll review the field mappings on the next screen before
            saving.
          </p>
          <div className="relative mb-3">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search tests by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8"
              autoFocus
            />
          </div>
          {error && (
            <div className="text-sm text-destructive mb-2">{error}</div>
          )}
          {scripts === null && !error && (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading tests…</p>
          )}
          {scripts !== null && scripts.length === 0 && !error && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Every test in this workspace already reads from a Set. To
              switch a test to this one, open that test's Test Data tab
              and pick this Set there.
            </p>
          )}
          {scripts !== null && scripts.length > 0 && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No tests match "{query}".
            </p>
          )}
          {filtered.length > 0 && (
            <div className="max-h-80 overflow-y-auto border rounded divide-y">
              {filtered.map((s) => (
                <button
                  key={s._id}
                  type="button"
                  onClick={() => onBindCreated(s._id)}
                  className="w-full text-left px-3 py-2 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none transition-colors"
                >
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Updated {new Date(s.updatedAt).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
